import { Component, OnInit, OnDestroy } from '@angular/core';
import { AlertConfig, AlertConfigService, PendingSignaturesPanelItem } from '../../../../core/services/api/alert-config.service';

const UNIT_FACTORS: Record<string, number> = { Minutos: 1, Horas: 60, Días: 1440 };
const POLLING_INTERVAL_MS = 60000;

@Component({
  selector: 'app-alert-config',
  templateUrl: './alert-config.component.html',
  styleUrls: ['./alert-config.component.scss']
})
export class AlertConfigComponent implements OnInit, OnDestroy {
  config: AlertConfig = { thresholdMinutes: 2880, jobFrequencyMinutes: 60, isEnabled: true };

  thresholdValue: number = 48;
  thresholdUnit: string = 'Horas';
  frequencyValue: number = 60;
  frequencyUnit: string = 'Minutos';

  pendingItems: PendingSignaturesPanelItem[] = [];

  showSpinner = false;
  pendingLoading = true;
  successMessage: string = null;
  errorMessage: string = null;
  pendingError: string = null;

  private pollingTimer: any;

  constructor(private alertConfigService: AlertConfigService) {}

  async ngOnInit() {
    this.showSpinner = true;
    try {
      this.config = await this.alertConfigService.getConfig();
      this.initFields();
    } catch {
      this.errorMessage = 'No se pudo cargar la configuración.';
    } finally {
      this.showSpinner = false;
    }
    await this.loadPending();
    this.pollingTimer = setInterval(() => this.loadPending(), POLLING_INTERVAL_MS);
  }

  ngOnDestroy() {
    if (this.pollingTimer) clearInterval(this.pollingTimer);
  }

  private initFields() {
    if (this.config.thresholdMinutes % 1440 === 0) {
      this.thresholdValue = this.config.thresholdMinutes / 1440;
      this.thresholdUnit = 'Días';
    } else if (this.config.thresholdMinutes % 60 === 0) {
      this.thresholdValue = this.config.thresholdMinutes / 60;
      this.thresholdUnit = 'Horas';
    } else {
      this.thresholdValue = this.config.thresholdMinutes;
      this.thresholdUnit = 'Minutos';
    }

    if (this.config.jobFrequencyMinutes > 60 && this.config.jobFrequencyMinutes % 60 === 0) {
      this.frequencyValue = this.config.jobFrequencyMinutes / 60;
      this.frequencyUnit = 'Horas';
    } else {
      this.frequencyValue = this.config.jobFrequencyMinutes;
      this.frequencyUnit = 'Minutos';
    }
  }

  get isThresholdInvalid(): boolean {
    return !this.thresholdValue || this.thresholdValue < 1;
  }

  get isFrequencyInvalid(): boolean {
    return !this.frequencyValue || this.frequencyValue < 1;
  }

  get isFormValid(): boolean {
    return !this.isThresholdInvalid && !this.isFrequencyInvalid;
  }

  async save() {
    this.successMessage = null;
    this.errorMessage = null;
    this.showSpinner = true;
    try {
      const thresholdMinutes = Math.round(Number(this.thresholdValue) * UNIT_FACTORS[this.thresholdUnit]);
      const jobFrequencyMinutes = Math.round(Number(this.frequencyValue) * UNIT_FACTORS[this.frequencyUnit]);
      await this.alertConfigService.updateConfig({ thresholdMinutes, jobFrequencyMinutes, isEnabled: this.config.isEnabled });
      this.config.thresholdMinutes = thresholdMinutes;
      this.config.jobFrequencyMinutes = jobFrequencyMinutes;
      this.successMessage = 'Configuración guardada correctamente.';
    } catch {
      this.errorMessage = 'Error al guardar la configuración.';
    } finally {
      this.showSpinner = false;
    }
  }

  async loadPending() {
    this.pendingError = null;
    this.pendingLoading = true;
    try {
      const result = await this.alertConfigService.getPendingSignatures(1, 50);
      this.pendingItems = result.items ?? [];
    } catch {
      this.pendingError = 'No se pudo cargar la lista de firmas pendientes.';
    } finally {
      this.pendingLoading = false;
    }
  }

  formatMinutes(minutes: number): string {
    if (minutes < 60) return `${Math.round(minutes)}min`;
    const hours = Math.floor(minutes / 60);
    const remaining = Math.round(minutes % 60);
    return remaining > 0 ? `${hours}h ${remaining}min` : `${hours}h`;
  }
}
